/* Nudge composer — lets the human agent inject a system message into the
 * AI's live LLM context mid-call. Useful for "pitch the most generous
 * offer", "probe deeper on the bill complaint", "transfer-to-human if
 * they ask again", etc. POSTs to /api/calls/:id/nudge; the worker looks
 * up the Telnyx call_control_id and proxies to the bot, which pushes a
 * `[Agent guidance: ...]` message into the running LLMContext so the
 * next assistant turn sees it.
 *
 * Sits below the stage card on the canvas during stages where steering
 * still matters (everything except close). Disabled when the call has
 * ended — at that point the live context is gone. */

import { type FormEvent, useState } from "react";

import { API_URL } from "../lib/api";
import { agentHeaders, useAgentIdentity } from "./identity";

type SendState =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "sent"; at: number }
  | { kind: "error"; message: string };

const SUGGESTIONS = [
  "Pitch our most generous retention offer.",
  "Probe deeper on the customer's actual concern before offering anything.",
  "If they keep declining, transfer to a human supervisor.",
  "Acknowledge frustration before pitching anything else.",
];

export function NudgeComposer({
  callId,
  ended,
}: {
  callId: number;
  ended: boolean;
}) {
  const { identity } = useAgentIdentity();
  const [text, setText] = useState("");
  const [state, setState] = useState<SendState>({ kind: "idle" });

  async function send(e: FormEvent) {
    e.preventDefault();
    const body = text.trim();
    if (!body || state.kind === "busy" || ended) return;
    setState({ kind: "busy" });
    try {
      const r = await fetch(`${API_URL}/api/calls/${callId}/nudge`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...agentHeaders(identity),
        },
        body: JSON.stringify({ text: body }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        const detail =
          (data as { detail?: string; error?: string }).detail ??
          (data as { detail?: string; error?: string }).error ??
          `nudge failed (${r.status})`;
        setState({ kind: "error", message: detail });
        return;
      }
      setText("");
      setState({ kind: "sent", at: Date.now() });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (ended) return null;

  const disabled = state.kind === "busy";

  return (
    <section
      id="agent-desk-nudge"
      className="agent-desk-nudge"
      aria-label="Nudge the AI"
    >
      <div className="agent-desk-nudge-eyebrow">NUDGE THE AI</div>
      <div className="agent-desk-nudge-hint">
        Type guidance for the next assistant turn — it lands as a system
        note in the LLM context. Use sparingly.
      </div>

      <form className="agent-desk-nudge-form" onSubmit={send}>
        <textarea
          id="agent-desk-nudge-input"
          className="agent-desk-nudge-input"
          rows={2}
          value={text}
          placeholder="e.g. Pitch the unlimited+ plan; they care about cost."
          onChange={(e) => setText(e.target.value)}
          disabled={disabled}
        />
        <button
          id="agent-desk-nudge-send-btn"
          type="submit"
          className="agent-desk-nudge-send"
          disabled={disabled || !text.trim()}
        >
          {state.kind === "busy" ? "Sending…" : "Send nudge"}
        </button>
      </form>

      <div className="agent-desk-nudge-suggestions">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            className="agent-desk-nudge-suggestion"
            onClick={() => setText(s)}
            disabled={disabled}
            title="Use this as a starting point"
          >
            {s}
          </button>
        ))}
      </div>

      {state.kind === "sent" && (
        <div className="agent-desk-nudge-status agent-desk-nudge-sent">
          Sent — the AI will see it on its next turn.
        </div>
      )}
      {state.kind === "error" && (
        <div className="dial-err">{state.message}</div>
      )}
    </section>
  );
}
